import React, { useState } from 'react';
import { universityData } from '@/resources/colleges_15';
import { AnalysisData } from '@/types';
import { DraftVersion } from '@/services/draftService';
import { timestampToDate } from '@/utils/common.utils';

interface DraftListProps {
  drafts: DraftVersion[]
  autofillDraft: (draft: AnalysisData) => void;
  universityId: number;
  prompt: string;
}

const DraftList: React.FC<DraftListProps> = ({ drafts, autofillDraft, universityId, prompt }) => {
  const [activeKey, setActiveKey] = useState<number | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedDraft, setSelectedDraft] = useState<DraftVersion | null>(null);

  const handleChange = (key: number) => {
    setActiveKey(key === activeKey ? null : key);
  };

  const showModal = (draftDetails: DraftVersion) => {
    setSelectedDraft(draftDetails);
    setIsModalVisible(true);
  };

  const handleOk = () => {

    console.log({selectedDraft})
    if (selectedDraft) {
      autofillDraft({
      prompt: selectedDraft.prompt_text,
      wordCount: selectedDraft.word_count,
      promptId: selectedDraft.prompt_id,
      draftId: selectedDraft.draft_id.toString(),
      essay: selectedDraft.essay,
      universityId,
      draftVersionId: selectedDraft.id,
      draftStatus: selectedDraft.draft_status,
      });
    }
    setIsModalVisible(false);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };



  return (
    <div className="draft-list">
      {drafts.length === 0 && prompt ? (
        <div className="text-center mt-10" style={{ color: 'var(--text-tertiary)' }}>
          <svg className="mx-auto h-8 w-8 mb-4" style={{ color: 'var(--text-tertiary)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <div style={{ color: 'var(--text-secondary)' }}>No matching drafts found for current prompt.</div>
        </div>
      ) : (
        <div className="space-y-4">
          {drafts.map((draft) => {

           

            const draftType = draft.draft_name.startsWith('Supplement') ? 'supplement' : 'common_app';


            const firstFiveWords = draft.essay ? draft.essay.split(/\s+/).slice(0, 5).join(' ') : '';

            return (
              <div key={draft.id} className="border rounded-lg" style={{ borderColor: 'var(--border-primary)' }}>
                <div
                  className="p-4 cursor-pointer hover:bg-[var(--surface-hover)] transition-colors flex justify-between items-center"
                  onClick={() => handleChange(draft.draft_id)}
                >

                  <div className="flex-1">
                    <div className="font-medium" style={{ color: 'var(--text-primary)' }}>{draft.draft_name} - {timestampToDate(draft.created_at)}</div>
                    <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>{firstFiveWords}...</div>
                  </div>
                  <svg 
                    className={`w-5 h-5 transition-transform ${activeKey === draft.draft_id ? 'rotate-180' : ''}`}
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                {activeKey === draft.draft_id && (
                  <div className="border-t p-4" style={{ borderColor: 'var(--border-primary)' }}>
                    <div
                      className="rounded-lg p-4 cursor-pointer hover:bg-[var(--surface-hover)] transition-colors"
                      style={{ background: 'var(--surface-hover)' }}
                      onClick={() => showModal(draft)}
                    >
                      <div className="mb-3">
                        <span className="px-2 py-1 rounded-full text-xs"
                          style={draftType === 'common_app'
                            ? { background: 'rgba(59, 130, 246, 0.12)', color: 'var(--info-dark)' }
                            : { background: 'rgba(139, 92, 246, 0.12)', color: 'var(--purple-dark)' }
                          }>
                          {draftType === 'common_app' ? 'Common App' : 'Supplement'}
                        </span>
                      </div>
                      <div className="space-y-2">
                        <div>
                          <span className="font-medium" style={{ color: 'var(--text-primary)' }}>Prompt:</span>
                          <p className="mt-1" style={{ color: 'var(--text-secondary)' }}>{draft.prompt_text}</p>
                        </div>
                        <div>
                          <span className="font-medium" style={{ color: 'var(--text-primary)' }}>Essay:</span>
                          <p className="mt-1" style={{ color: 'var(--text-secondary)' }}>{draft.essay}</p>
                        </div>
                        <div>
                          <span className="font-medium" style={{ color: 'var(--text-primary)' }}>Word Count:</span>
                          <span className="ml-2 px-2 py-1 rounded-full text-xs" style={{ background: 'rgba(59, 130, 246, 0.12)', color: 'var(--info-dark)' }}>
                            {draft.word_count} words
                          </span>
                        </div>
                        <div>
                          <span className="font-medium" style={{ color: 'var(--text-primary)' }}>Status:</span>
                          <span className="ml-2 px-2 py-1 rounded-full text-xs"
                            style={draft.draft_status === 'Mandatory'
                              ? { background: 'rgba(239, 68, 68, 0.12)', color: 'var(--danger-dark)' }
                              : { background: 'rgba(34, 197, 94, 0.12)', color: 'var(--success-light)' }
                            }>
                            {draft.draft_status}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modal to confirm autofill */}
      {isModalVisible && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="rounded-lg p-6 max-w-md w-full mx-4 overflow-y-auto max-h-[620px]" style={{ background: 'var(--surface-primary)' }}>
            <h3 className="text-lg font-medium mb-4" style={{ color: 'var(--text-primary)' }}>Complete Draft</h3>
            {selectedDraft && (
              <div className="space-y-3 mb-6">
                <div>
                  <span className="font-medium" style={{ color: 'var(--text-primary)' }}>Prompt:</span>
                  <p className="mt-1" style={{ color: 'var(--text-secondary)' }}>{selectedDraft.prompt_text}</p>
                </div>
                <div>
                  <span className="font-medium" style={{ color: 'var(--text-primary)' }}>Essay:</span>
                  <p className="mt-1" style={{ color: 'var(--text-secondary)' }}>{selectedDraft.essay}</p>
                </div>
                <div>
                  <span className="font-medium" style={{ color: 'var(--text-primary)' }}>Word Count:</span>
                  <span className="ml-2 px-2 py-1 rounded-full text-xs" style={{ background: 'rgba(59, 130, 246, 0.12)', color: 'var(--info-dark)' }}>
                    {selectedDraft.word_count} words
                  </span>
                </div>
              </div>
            )}
            <div className="flex gap-3">
              <button
                onClick={handleOk}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md transition-colors"
              >
                Use Draft
              </button>
              <button
                onClick={handleCancel}
                className="flex-1 px-4 py-2 rounded-md transition-colors hover:bg-[var(--surface-hover)]"
                style={{ background: 'var(--surface-hover)', color: 'var(--text-primary)' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DraftList; 